// Libraries
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// MUI
import Grid from '@mui/material/Grid';

// Core
import { dayFormat } from '../../core/constants';
import { ResourceTextApplication } from '../../core/resources';

// Common
import {
    ADialog,
    ADialogTitle,
    ADialogContent,
    ADialogSubHeading,
    ADialogActions,
} from '../../common/dialog';
import { InfoBlock } from '../../common/typography';

// Interfaces
import { IEvent } from '../../interfaces/IEvent';

// Store
import { useAppSelector } from '../../store/hooks';

// API
// Feature - Editable content
import RenderContent from '../../editableContent/RenderContent';

// Feature - Event
import EventMeetingLink from './EventMeetingLink';

interface IEventDialogProps {
    event: IEvent;
    onClose: () => void;
}

/** Is used in Anelma Calendar feature to only render event contents. Registration data isn't needed. JIRA ticket: AN-2577.
 *
 */
export default function EventDialog({ event, onClose }: IEventDialogProps) {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaGeneral', 'AnelmaEvents']);

    const userLocale = useAppSelector((state) => state.configuration.selectedCulture);
    const userLoginData = useAppSelector((state) => state.loginStatus.data);

    const getLocalizedEventBodyText = () => {
        if (userLocale === 'fi') {
            return event.BodyTextFI;
        } else if (userLocale === 'sv-SE') {
            return event.BodyTextSE;
        } else if (userLocale === 'en-GB') {
            return event.BodyTextEN;
        }
    };

    const getLocalizedEventHeader = () => {
        if (userLocale === 'fi') {
            return event.HeaderFI;
        } else if (userLocale === 'sv-SE') {
            return event.HeaderSE;
        } else if (userLocale === 'en-GB') {
            return event.HeaderEN;
        }
    };

    const getDialogActions = () => {
        return (
            <ADialogActions
                buttons={{
                    left: [
                        {
                            enabledWhileLoading: true,
                            label: t('AnelmaGeneral:1009'),
                            onClick: onClose,
                            type: 'cancel',
                        },
                    ],
                    right: [],
                }}
            ></ADialogActions>
        );
    };

    const getLocation = () => {
        switch (userLocale) {
            case 'fi':
            case 'fi-FI':
                return event.LocationFI;
            case 'en-GB':
                return event.LocationEN;
            case 'sv-SE':
                return event.LocationSE;
        }
    };

    return (
        <ADialog open onClose={() => onClose()}>
            <ADialogTitle>{t('AnelmaEvents:1010')}</ADialogTitle>

            <ADialogContent size='md'>
                <ADialogSubHeading>{getLocalizedEventHeader()}</ADialogSubHeading>

                <Grid container style={{ paddingLeft: '40px' }}>
                    <Grid size={{ sm: 4 }}>
                        <InfoBlock
                            label={t('AnelmaEvents:1010')}
                            value={`${moment(event.EventValidFrom).format(dayFormat)} - ${moment(
                                event.EventValidDue
                            ).format(dayFormat)}`}
                        />
                    </Grid>
                    <Grid size={{ sm: 4 }}>
                        <InfoBlock label={t('AnelmaEvents:1004')} value={getLocation()} />
                    </Grid>
                    {event.OnlineEvent && (
                        <Grid size={{ sm: 4 }} sx={{ marginBottom: '10px' }}>
                            <EventMeetingLink meetingLink={event.OnlineLink} />
                        </Grid>
                    )}
                </Grid>

                <Grid container style={{ padding: '40px' }}>
                    <RenderContent publicSite={!userLoginData.loggedIn} id={event.ContentId} />

                    {getLocalizedEventBodyText()}
                </Grid>
            </ADialogContent>

            {getDialogActions()}
        </ADialog>
    );
}
